import { getSearchParamsString } from '@reapitform/utils'
import { getUrl, usePlatformGet } from '@reapitform/hooks'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { IPaginationParams, IResponseWithPagination } from '../types'
import { AgreementListItem } from '../types/agreements'
import { TTablePagination } from '@reapitform/common-ui'

const filteringAgreementData = (query: string, data: AgreementListItem[]) =>
  new Promise<AgreementListItem[]>((resolve) => {
    const queryLowerCase = query.toLowerCase()
    const filteredData = data.filter((currentAgreement) => {
      const address = currentAgreement.propertyAddress
      const mainAddress: string =
        `${address.unitNo} ${address.streetNo} ${address?.street} ${address.streetType}`.toLowerCase()
      const subAddress: string = `${address.suburb} ${address.state} ${address.postCode}`.toLowerCase()
      const signee: string = currentAgreement.recipients?.join('').toLowerCase()

      return (
        mainAddress.includes(queryLowerCase) || subAddress.includes(queryLowerCase) || signee.includes(queryLowerCase)
      )
    })

    resolve(filteredData)
  })

export const AGREEMENT_QUERY_KEY = ['agreement-list']

export const useAgreementList = (paramsProps: IPaginationParams) => {
  const [data, setData] = useState<AgreementListItem[] | null>(null)
  const [isManualFiltering, setIsManualFiltering] = useState<boolean>(false)
  const { query, ...params } = paramsProps
  const queryClient = useQueryClient()

  const searchParams = getSearchParamsString({ ...params })
  const path: string = `/api/Document${searchParams}`
  const [result, loading, errorString] = usePlatformGet<IResponseWithPagination<AgreementListItem>>({
    path,
    retry: false,
    queryKeys: AGREEMENT_QUERY_KEY,
  })

  const queryKey = useMemo(getUrl(path), [path])

  const pagination: TTablePagination | undefined = result?._embedded
    ? {
        currentPage: result?.pageNumber,
        numberPages: result?.totalPageCount,
        dataCount: result.totalCount,
      }
    : undefined

  const updateCacheData = (data: AgreementListItem[] | ((preData: AgreementListItem[]) => AgreementListItem[])) => {
    if (!queryClient) return

    queryClient.setQueryData([queryKey], (prevData: IResponseWithPagination<AgreementListItem>) => {
      let newValue = data
      if (typeof data === 'function') newValue = data(prevData._embedded)

      return { ...prevData, _embedded: newValue }
    })

    queryClient.invalidateQueries({ queryKey: [queryKey] })
  }

  const getFilteredAgreementData = async ({ query, data }: { query?: string | null; data: AgreementListItem[] }) => {
    const filteredData = query ? await filteringAgreementData(query, data) : data
    setIsManualFiltering(false)
    return filteredData
  }

  useEffect(() => {
    if (!result?._embedded) return setData(null)
    getFilteredAgreementData({ query, data: result._embedded }).then((filteredData) => {
      setData(filteredData)
    })
  }, [query, result?._embedded])

  useEffect(() => {
    setIsManualFiltering(true)
  }, [params.pageNumber, params.pageSize, params.sortBy, params.sortByDirection, params.status])

  useEffect(() => {
    setIsManualFiltering(false)
  }, [result?._links.self])

  return {
    data: data,
    pagination,
    loading: loading || isManualFiltering,
    errorString,
    updateCacheData,
  }
}
